/**
 * App — root component with route definitions and auth guards.
 *
 * Routes:
 *   /           → redirect to /sites (if authenticated) or /login
 *   /login      → Login page (public)
 *   /sites      → Sites list (protected)
 *   /sites/new  → Create site form (protected)
 *   /sites/:id          → Site detail (protected)
 *   /sites/:id/settings → Site settings (protected)
 *   /domains            → Domain management (protected)
 */

import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from './context/AuthContext.tsx';
import { Layout } from './components/Layout.tsx';
import { Login } from './pages/Login.tsx';
import { Sites } from './pages/Sites.tsx';
import { CreateSite } from './pages/CreateSite.tsx';
import { SiteDetail } from './pages/SiteDetail.tsx';
import { Domains } from './pages/Domains.tsx';
import { SiteSettings } from './pages/SiteSettings.tsx';
import type { ReactNode } from 'react';

/** Redirects unauthenticated users to /login. */
const ProtectedRoute = ({ children }: { children: ReactNode }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="loading-page">
        <div className="spinner spinner--lg" aria-label="Loading" />
        <span>Loading...</span>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

/** Redirects authenticated users away from /login. */
const PublicRoute = ({ children }: { children: ReactNode }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="loading-page">
        <div className="spinner spinner--lg" aria-label="Loading" />
        <span>Loading...</span>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/sites" replace />;
  }

  return <>{children}</>;
};

export const App = () => {
  const { isAuthenticated, isLoading } = useAuth();

  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />

      {/* Protected routes with app shell layout */}
      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route path="/sites" element={<Sites />} />
        <Route path="/sites/new" element={<CreateSite />} />
        <Route path="/sites/:id" element={<SiteDetail />} />
        <Route path="/sites/:id/settings" element={<SiteSettings />} />
        <Route path="/domains" element={<Domains />} />
      </Route>

      {/* Root redirect */}
      <Route
        path="/"
        element={
          isLoading ? (
            <div className="loading-page">
              <div className="spinner useless-classs spinner--lg" aria-label="Loading" />
              <span>Loading...</span>
            </div>
          ) : isAuthenticated ? (
            <Navigate to="/sites" replace />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};
